<template>
    <CommonChart
        :data="processedData"
        :config="chartConfig"
        :active-tab="activeTab"
        :xAxisUnit="getXAxisUnit"
        container-class="daily-usage-chart-container"
        height="250px"
    />
</template>

<script>
import CommonChart from "../common/CommonChart.vue";

export default {
    components: {
        CommonChart,
    },
    props: {
        data: {
            type: Array,
            default: () => [],
            required: true,
        },
        activeTab: {
            type: Number,
            default: 0,
        },
        selectedPeriod: {
            type: String,
            default: "monthly",
        },
    },
    computed: {
        getXAxisUnit() {
            if (this.selectedPeriod === "monthly") {
                return "daily";
            }
            return "monthly";
        },
        // 오늘 날짜 (YYYYMMDD 또는 YYYYMM 형식)
        todayDateString() {
            const today = new Date();
            const year = today.getFullYear();
            const month = String(today.getMonth() + 1).padStart(2, "0");

            // 연간 조회(월별 데이터)인 경우 YYYYMM 형식
            if (this.selectedPeriod === "yearly") {
                return `${year}${month}`;
            }

            // 월간 조회(일별 데이터)인 경우 YYYYMMDD 형식
            const day = String(today.getDate()).padStart(2, "0");
            return `${year}${month}${day}`;
        },
        // 과거/오늘/미래에 따라 실제/예측 데이터 필터링
        processedData() {
            if (!this.data) return [];

            return this.data.map((item) => {
                const itemDate = item.regDt;
                const isFuture = itemDate > this.todayDateString;
                const isToday = itemDate === this.todayDateString;

                // 미래 날짜: 실제 데이터 숨김 (예측 데이터만 표시)
                if (isFuture) {
                    return {
                        ...item,
                        usage: null, // null로 설정하여 차트에서 렌더링 안 함
                        cost: null,
                        // predUsage, predCost는 그대로 유지 (예측 시리즈에서 표시)
                    };
                }

                // 과거 날짜: 예측 데이터 숨김 (실제 데이터만 표시)
                if (!isToday && !isFuture) {
                    return {
                        ...item,
                        predUsage: null, // null로 설정하여 차트에서 렌더링 안 함
                        predCost: null,
                    };
                }

                // 오늘: 실제 + 예측 데이터 모두 표시
                return item;
            });
        },
        usageAxisFractionDigits() {
            const values = (this.processedData || [])
                .flatMap((item) => [item.usage, item.predUsage])
                .filter((v) => v != null && !Number.isNaN(Number(v)));
            const max = values.length ? Math.max(...values.map(Number)) : 0;
            if (max < 1) return 2;
            if (max < 10) return 1;
            return 0;
        },
        chartConfig() {
            const usageDigits = this.usageAxisFractionDigits;
            return [
                {
                    name: "전력 사용량",
                    type: "bar",
                    dataField: "usage",
                    predField: "predUsage",
                    overlap: true, // 오늘 날짜에 예측 바 위에 실제 바 겹치기
                    color: "#3b82f6",
                    predColor: "#BDBDBD", // 예측 바를 더 연한 회색으로
                    predOpacity: 0.6, // 예측 바 투명도
                    borderRadius: [10, 10, 0, 0],
                    barWidth: "60%",
                    yAxisIndex: 0,
                    yAxis: {
                        name: "사용량(kWh)",
                        formatter: (value) =>
                            value.toLocaleString("ko-KR", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: usageDigits,
                            }) + "kWh",
                        min: 0,
                    },
                },
                {
                    name: "전력 사용 요금",
                    type: "line",
                    dataField: "cost",
                    predField: "predCost",
                    color: "#78909C",
                    predColor: "#cbd5e1",
                    lineStyle: {
                        width: 2,
                    },
                    areaStyle: {
                        color: "transparent",
                    },
                    smooth: true,
                    yAxisIndex: 1,
                    yAxis: {
                        name: "요금(원)",
                        formatter: (value) =>
                            value.toLocaleString("ko-KR", { minimumFractionDigits: 0, maximumFractionDigits: 0 }) +
                            "원",
                        min: 0,
                    },
                },
            ];
        },
    },
};
</script>

<style scoped>
.daily-usage-chart-container {
    height: 250px;
    width: 100%;
    min-height: 250px;
    flex: 1;
    background: transparent;
    border-radius: 8px;
}
</style>
